import { useFrisbiiPayment } from '@/hooks/useFrisbiiPayment';
import useOrderCreate from '@/hooks/useOrderCreate';
import useUserAuth from '@/hooks/useUserAuth';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { mergeProps } from '@/utils/merge';
import { ComponentProps, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToggle } from 'react-use';
import { ArticlePaywall } from './ArticlePaywall';
import { FrisbiiError } from 'lib/errors/FrisbiiError';
import { Icon } from '@/components/Icon';
import useCookies from '@/hooks/useCookies';

type BasePaywallProps = StandaloneComponentProps<typeof ArticlePaywall> & {
  description?: ReactNode;
  headline?: ReactNode;
  logo?: ReactNode;
  dialog?: ComponentProps<typeof ArticlePaywall.Dialog>;
  features?: ReactNode[];
  successfulPayment?: ComponentProps<typeof ArticlePaywall.SuccessfulPayment>;
  form?: ComponentProps<typeof ArticlePaywall.Form>;
};

type KlarnaPaywallProps = BasePaywallProps & {
  paymentMethod: 'klarna';
  klarnaPayment?: ComponentProps<typeof ArticlePaywall.KlarnaPayment>;
  frisbiiPayment?: never;
  handleSuccessPayment?: (
    ref: React.RefObject<HTMLDialogElement>,
    toggleIsPaymentSuccessful: (value: boolean) => void,
  ) => ComponentProps<typeof ArticlePaywall.KlarnaPayment>['handleSuccess'];
  handleFailurePayment?: (
    ref: React.RefObject<HTMLDialogElement>,
  ) => ComponentProps<typeof ArticlePaywall.KlarnaPayment>['handleFailure'];
};

type FrisbiiPaywallProps = BasePaywallProps & {
  paymentMethod: 'frisbii';
  frisbiiPayment?: ComponentProps<typeof ArticlePaywall.FrisbiiPayment>;
  klarnaPayment?: never;
  handleSuccessPayment?: (
    ref: React.RefObject<HTMLDialogElement>,
    toggleIsPaymentSuccessful: (value: boolean) => void,
  ) => ComponentProps<typeof ArticlePaywall.FrisbiiPayment>['handleSuccess'];
  handleFailurePayment?: (
    ref: React.RefObject<HTMLDialogElement>,
  ) => ComponentProps<typeof ArticlePaywall.FrisbiiPayment>['handleFailure'];
};

type FrisbiiSuccessHandler = NonNullable<ComponentProps<typeof ArticlePaywall.FrisbiiPayment>['handleSuccess']>;
type FrisbiiFailureHandler = NonNullable<ComponentProps<typeof ArticlePaywall.FrisbiiPayment>['handleFailure']>;

type KlarnaSuccessHandler = NonNullable<ComponentProps<typeof ArticlePaywall.KlarnaPayment>['handleSuccess']>;
type KlarnaFailureHandler = NonNullable<ComponentProps<typeof ArticlePaywall.KlarnaPayment>['handleFailure']>;

export type ArticlePaywallProps = KlarnaPaywallProps | FrisbiiPaywallProps;

const paymentContainerId = 'payment-container';
export const StandaloneArticlePaywall: StandaloneComponent<ArticlePaywallProps> = ({
  description,
  headline,
  logo,
  dialog,
  features,
  klarnaPayment,
  frisbiiPayment,
  handleSuccessPayment,
  handleFailurePayment,
  successfulPayment,
  form,
  paymentMethod,
  ...props
}) => {
  const { isUserLoggedIn, alltIdUserCode, createPasswordlessAccount, resetAlltIdUserControlCode } = useUserAuth();
  const { createOrder } = useOrderCreate();
  const { createFrisbiiSession, frisbiiSession } = useFrisbiiPayment();
  const [isPaymentSuccessful, toggleIsPaymentSuccessful] = useToggle(false);
  const [isProcessing, toggleIsProcessing] = useToggle(false);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const paywallRef = useRef<HTMLDialogElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isKlarnaPayment = paymentMethod === 'klarna';
  const isFrisbiiPayment = paymentMethod === 'frisbii';
  const canShowFrisbiiPayment = isFrisbiiPayment && (isUserLoggedIn || frisbiiSession);
  const canShowKlarnaPayment = isKlarnaPayment && !!alltIdUserCode;
  const canAutoTriggerPayment = canShowFrisbiiPayment && !isUserLoggedIn;

  const formOnSubmitHandler = useCallback(
    async (...args: any[]) => {
      setError(null);
      toggleIsProcessing();
      if (isKlarnaPayment) {
        await createPasswordlessAccount(...(args as Parameters<typeof createPasswordlessAccount>)).catch((e: Error) => {
          setError('Förlåt, ett fel har uppstått. Vänligen försök igen om en stund.');
        });
        toggleIsProcessing();
        return;
      }

      if (isFrisbiiPayment) {
        await createFrisbiiSession(...(args as Parameters<typeof createFrisbiiSession>)).catch((e: FrisbiiError) => {
          switch (e.code) {
            case 'account_exists':
              toggleIsPaymentSuccessful(true);
              break;
            default:
              setError('Förlåt, ett fel har uppstått. Vänligen försök igen om en stund.');
              break;
          }
        });
        toggleIsProcessing();
        return;
      }
    },
    [isKlarnaPayment, isFrisbiiPayment, createFrisbiiSession, createPasswordlessAccount],
  );

  const klarnaSuccessHandler = useCallback<KlarnaSuccessHandler>(
    async (response: any) => {
      await createOrder(alltIdUserCode!, response.token);
      toggleIsPaymentSuccessful(true);
    },
    [alltIdUserCode, createOrder, toggleIsPaymentSuccessful],
  );

  const klarnaFailureHandler = useCallback<KlarnaFailureHandler>(
    async (event: any) => {
      if (event?.status === 409) {
        setError('Den angivna e-postadressen har redan en aktiv prenumeration.');
        resetAlltIdUserControlCode();
        toggleIsPaymentSuccessful(false);
        toggleIsProcessing(false);
        return;
      }
      dialogRef.current?.showModal();
    },
    [alltIdUserCode, createOrder, toggleIsPaymentSuccessful],
  );

  const frisbiiSuccessHandler = useCallback<FrisbiiSuccessHandler>(
    async (successResponse) => {
      toggleIsPaymentSuccessful(true);

      if (successResponse?.redirect_to) {
        window.location.href = successResponse.redirect_to;
      }
    },
    [handleSuccessPayment, toggleIsPaymentSuccessful, paywallRef],
  );

  const frisbiiFailureHandler = useCallback<FrisbiiFailureHandler>(
    async (errorMessage?: string) => {
      toggleIsPaymentSuccessful(false);

      handleFailurePayment?.(dialogRef)?.();

      paywallRef.current?.scrollIntoView({ block: 'center', inline: 'nearest' });
      setError(errorMessage || 'Förlåt, ett fel har uppstått. Vänligen försök igen om en stund.');
    },
    [handleFailurePayment, toggleIsPaymentSuccessful, paywallRef],
  );

  const getPaymentContent = () => {
    if (isPaymentSuccessful) {
      return (
        <ArticlePaywall.SuccessfulPayment
          {...mergeProps(successfulPayment, { options: props.options?.$successfulPayment })}
        />
      );
    }

    if (canShowFrisbiiPayment) {
      return (
        <ArticlePaywall.FrisbiiPayment
          handleSuccess={frisbiiSuccessHandler}
          handleFailure={frisbiiFailureHandler}
          autoTrigger={canAutoTriggerPayment}
          {...mergeProps(
            {
              options: props.options?.$frisbiiPayment,
              button: { content: 'Betala', options: { size: 'large', disabled: isProcessing } },
            },
            frisbiiPayment,
            { button: { isLoading: isProcessing } },
          )}
        />
      );
    }

    if (canShowKlarnaPayment) {
      return (
        <ArticlePaywall.KlarnaPayment
          handleSuccess={klarnaSuccessHandler}
          handleFailure={klarnaFailureHandler}
          paymentContainerId={paymentContainerId}
          {...mergeProps(
            {
              options: props.options?.$klarnaPayment,
              button: { content: 'Betala', options: { size: 'large', disabled: isProcessing } },
            },
            klarnaPayment,
            { button: { isLoading: isProcessing } },
          )}
        />
      );
    }

    return (
      <ArticlePaywall.Form
        onSubmit={formOnSubmitHandler}
        {...mergeProps({ options: { ...props.options?.$form, $button: { disabled: isProcessing } } }, form, {
          button: { isLoading: isProcessing },
        })}
      />
    );
  };

  const showContentWhenNotPaid = !isPaymentSuccessful;

  return (
    <ArticlePaywall ref={paywallRef} {...props}>
      {logo && <ArticlePaywall.Logo>{logo}</ArticlePaywall.Logo>}
      {headline && showContentWhenNotPaid && <ArticlePaywall.Headline>{headline}</ArticlePaywall.Headline>}
      {description && showContentWhenNotPaid && <ArticlePaywall.Description>{description}</ArticlePaywall.Description>}
      {features?.length && showContentWhenNotPaid && (
        <ArticlePaywall.FeaturesList>
          {features.map((feature, index) => (
            <ArticlePaywall.Feature key={index}>{feature}</ArticlePaywall.Feature>
          ))}
        </ArticlePaywall.FeaturesList>
      )}
      {error && (
        <ArticlePaywall.Error>
          <Icon name={'error'} />
          {error}
        </ArticlePaywall.Error>
      )}
      {getPaymentContent()}
      {dialog && <ArticlePaywall.Dialog options={{ ref: dialogRef }} {...dialog} />}
    </ArticlePaywall>
  );
};
